import { DirectiveBinding, Directive, createVNode, render, VNode } from 'vue';
import CxTooltip from './CxTooltip.vue';
import { TooltipProps } from './TooltipProps';

// Delay (ms) before an intentionally-hovered tooltip is rendered. Prevents tooltips
// from flashing as the cursor merely passes over elements. See ITS-21143.
const DEFAULT_SHOW_DELAY = 400;

const calculateAbsoluteOffset = (el: HTMLElement) => {
  const rect = el.getBoundingClientRect();
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
};

type TooltipEntry = {
  mouseOverHandler: () => void;
  mouseLeaveHandler: () => void;
  tooltip: VNode;
  showTimeout: number | null;
};

type TooltipDirective = Directive<HTMLElement, TooltipProps> & { handlerMap: WeakMap<HTMLElement, TooltipEntry> }

export const CxTooltipDirective: TooltipDirective = {

  // keeps map of element: registered mouse handlers to unsubcribe from them when the parent component is unmouted
  handlerMap: new WeakMap(),

  mounted: (el: HTMLElement, binding: DirectiveBinding) => {
    let tooltip = createVNode(CxTooltip, binding.value);

    const showTooltip = () => {
      const offset = calculateAbsoluteOffset(el);
      // animationDelay is forced to 0: the hover-intent delay is now handled by the JS
      // timer below, so the CSS fade must play immediately once the tooltip is rendered
      // (otherwise the two delays would stack).
      const props = { ...binding.value, show: true, parentElement: el, top: offset.top, left: offset.left, animationDelay: 0 };
      tooltip = createVNode(CxTooltip, props);
      render(tooltip, el);
    };

    const handleMouseOver = () => {
      const entry = CxTooltipDirective.handlerMap.get(el);
      if (!entry || entry.showTimeout !== null) return;
      if (tooltip?.props?.show || !tooltip?.props?.text || binding.value.disabled) return;

      const delay = binding.value.showDelay ?? DEFAULT_SHOW_DELAY;
      entry.showTimeout = window.setTimeout(() => {
        entry.showTimeout = null;
        showTooltip();
      }, delay);
    };

    const handleMouseLeave = () => {
      const entry = CxTooltipDirective.handlerMap.get(el);
      if (!entry) return;

      // Cancel a pending show so a tooltip never appears when the cursor leaves before the delay elapses.
      if (entry.showTimeout !== null) {
        clearTimeout(entry.showTimeout);
        entry.showTimeout = null;
      }

      if (tooltip?.props?.show) {
        tooltip = createVNode(CxTooltip, { ...binding.value, show: false, parentElement: el });
        render(tooltip, el);
      }
    };

    CxTooltipDirective.handlerMap.set(el, { mouseOverHandler: handleMouseOver, mouseLeaveHandler: handleMouseLeave, tooltip, showTimeout: null });
    el.addEventListener('mouseover', handleMouseOver);
    el.addEventListener('mouseleave', handleMouseLeave);
  },
  updated: (el: HTMLElement, binding: DirectiveBinding) => {
    const offset = calculateAbsoluteOffset(el);
    const props = { ...binding.value, show: binding.value?.show || false, parentElement: el, top: offset.top, left: offset.left };
    const tooltip = createVNode(CxTooltip, props);
    render(tooltip, el);
  },

  unmounted: (el: HTMLElement) => {

    const entry = CxTooltipDirective.handlerMap.get(el);

    if (entry?.showTimeout != null) clearTimeout(entry.showTimeout);

    if (entry?.mouseOverHandler) el.removeEventListener('mouseover', entry.mouseOverHandler);
    if (entry?.mouseLeaveHandler) el.removeEventListener('mouseleave', entry.mouseLeaveHandler);

    // remove tooltip from DOM
    render(null, el);

    CxTooltipDirective.handlerMap.delete(el);
  }
};
